<!-- eslint-disable -->
<template>
  <v-dialog v-model="showDialog" fullscreen persistent>
    <div class="video-call-container">
      <!-- Remote video (full screen) -->
      <div class="remote-video-wrapper">
        <div v-if="!remoteVideoReady" class="waiting-overlay">
          <v-avatar size="80" color="primary" class="mb-4">
            <v-img v-if="otherUser?.avatarUrl" :src="otherUser.avatarUrl"></v-img>
            <span v-else class="text-h4">{{ otherUser?.displayName?.charAt(0) || '?' }}</span>
          </v-avatar>
          <div class="text-h6 text-white mb-2">{{ otherUser?.displayName || 'User' }}</div>
          <div class="text-body-2 text-white" style="opacity: 0.8">
            {{ isOutgoing ? $t('chat.calling') : $t('chat.connecting') }}
          </div>
          <v-progress-circular v-if="!isOutgoing || callState === 'connecting'" 
            indeterminate color="white" class="mt-4" size="32"></v-progress-circular>
        </div>
        <div id="remote-video" ref="remoteVideoRef" class="remote-video"></div>
      </div>

      <!-- Local video (picture-in-picture) -->
      <div class="local-video-wrapper" :class="{ 'expanded': !remoteVideoReady }">
        <div id="local-video" ref="localVideoRef" class="local-video"></div>
      </div>

      <!-- Call timer -->
      <div v-if="remoteVideoReady" class="call-timer">
        {{ callDuration }}
      </div>

      <!-- Controls -->
      <div class="call-controls">
        <v-btn 
          :icon="isMicOn ? 'mdi-microphone' : 'mdi-microphone-off'"
          :color="isMicOn ? 'white' : 'red'"
          size="large"
          variant="flat"
          class="control-btn"
          @click="toggleMic"
        ></v-btn>

        <v-btn 
          :icon="isCamOn ? 'mdi-video' : 'mdi-video-off'"
          :color="isCamOn ? 'white' : 'red'"
          size="large"
          variant="flat"
          class="control-btn"
          @click="toggleCam"
        ></v-btn>

        <v-btn 
          icon="mdi-phone-hangup"
          color="red"
          size="x-large"
          variant="flat"
          class="control-btn hangup-btn"
          @click="endCall"
        ></v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AgoraRTC from 'agora-rtc-sdk-ng'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  channelName: { type: String, required: true },
  otherUser: { type: Object, default: null },
  isOutgoing: { type: Boolean, default: true },
  appId: { type: String, required: true }
})

const emit = defineEmits(['update:modelValue', 'call-ended'])

// ─── STATE ─────────────────────────────────────────────
const showDialog = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const callState = ref('connecting')  // connecting | connected | ended
const remoteVideoReady = ref(false)
const isMicOn = ref(true)
const isCamOn = ref(true)
const callStartTime = ref(null)
const callDuration = ref('00:00')

const localVideoRef = ref(null)
const remoteVideoRef = ref(null)

let client = null
let localAudioTrack = null
let localVideoTrack = null
let durationTimer = null

// ─── AGORA CLIENT ──────────────────────────────────────

const initAgora = async () => {
  try {
    // Create client
    client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })

    // Event: remote user publishes
    client.on('user-published', async (user, mediaType) => {
      await client.subscribe(user, mediaType)
      console.log('Subscribed to remote user:', user.uid, mediaType)
      
      if (mediaType === 'video') {
        remoteVideoReady.value = true
        callState.value = 'connected'
        startDurationTimer()
        
        // Play remote video
        if (remoteVideoRef.value) {
          user.videoTrack.play(remoteVideoRef.value)
        }
      }
      if (mediaType === 'audio') {
        user.audioTrack.play()
      }
    })

    // Event: remote user unpublishes
    client.on('user-unpublished', (user, mediaType) => {
      console.log('Remote user unpublished:', user.uid, mediaType)
      if (mediaType === 'video') {
        remoteVideoReady.value = false
      }
    })

    // Event: remote user leaves
    client.on('user-left', (user) => {
      console.log('Remote user left:', user.uid)
      remoteVideoReady.value = false
      endCall()
    })

    // Create local tracks
    const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks()
    localAudioTrack = audioTrack
    localVideoTrack = videoTrack

    // Play local video preview
    if (localVideoRef.value) {
      localVideoTrack.play(localVideoRef.value)
    }

    // Join channel (uid 0 = auto-assign)
    await client.join(props.appId, props.channelName, null, 0)
    console.log('Joined Agora channel:', props.channelName)

    // Publish local tracks
    await client.publish([localAudioTrack, localVideoTrack])
    console.log('Published local tracks')

    callState.value = props.isOutgoing ? 'connecting' : 'connected'

  } catch (error) {
    console.error('Agora init error:', error)
    callState.value = 'ended'
  }
}

// ─── CONTROLS ──────────────────────────────────────────

const toggleMic = () => {
  if (localAudioTrack) {
    isMicOn.value = !isMicOn.value
    localAudioTrack.setEnabled(isMicOn.value)
  }
}

const toggleCam = () => {
  if (localVideoTrack) {
    isCamOn.value = !isCamOn.value
    localVideoTrack.setEnabled(isCamOn.value)
  }
}

const endCall = async () => {
  callState.value = 'ended'
  stopDurationTimer()

  try {
    // Close local tracks
    if (localAudioTrack) {
      localAudioTrack.close()
      localAudioTrack = null
    }
    if (localVideoTrack) {
      localVideoTrack.close()
      localVideoTrack = null
    }

    // Leave channel
    if (client) {
      await client.leave()
      client = null
    }
  } catch (error) {
    console.error('End call error:', error)
  }

  emit('call-ended')
  showDialog.value = false
}

// ─── DURATION TIMER ────────────────────────────────────

const startDurationTimer = () => {
  callStartTime.value = Date.now()
  durationTimer = setInterval(() => {
    const elapsed = Math.floor((Date.now() - callStartTime.value) / 1000)
    const mins = Math.floor(elapsed / 60).toString().padStart(2, '0')
    const secs = (elapsed % 60).toString().padStart(2, '0')
    callDuration.value = `${mins}:${secs}`
  }, 1000)
}

const stopDurationTimer = () => {
  if (durationTimer) {
    clearInterval(durationTimer)
    durationTimer = null
  }
}

// ─── LIFECYCLE ─────────────────────────────────────────

onMounted(() => {
  if (props.modelValue) {
    initAgora()
  }
})

watch(() => props.modelValue, (val) => {
  if (val) {
    initAgora()
  }
})

onUnmounted(() => {
  endCall()
})
</script>

<style scoped>
.video-call-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: #1a1a1a;
  overflow: hidden;
}

.remote-video-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.remote-video {
  width: 100%;
  height: 100%;
}

.waiting-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  z-index: 5;
}

.local-video-wrapper {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 120px;
  height: 160px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 10;
  transition: all 0.3s ease;
}

.local-video-wrapper.expanded {
  width: 180px;
  height: 240px;
}

.local-video {
  width: 100%;
  height: 100%;
}

.call-timer {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 16px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  z-index: 10;
}

.call-controls {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 20px;
  align-items: center;
  z-index: 10;
}

.control-btn {
  opacity: 0.9;
  transition: opacity 0.2s;
}

.control-btn:hover {
  opacity: 1;
}

.hangup-btn {
  width: 64px !important;
  height: 64px !important;
}
</style>